"use client";

import React, { useEffect, useRef, useState } from "react";
import { Clock, DollarSign, MousePointerClick, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Global Network",
  icon: DollarSign,
  description: "Strategic partnerships across six continents providing priority access to the world's most exclusive private terminals."
}, {
  title: "Industry Expertise",
  icon: MousePointerClick,
  description: "Specialized knowledge in Part 91 and 135 operations, ensuring regulatory compliance and peak performance."
}, {
  title: "Absolute Discretion",
  icon: ShieldCheck,
  description: "Our commitment to privacy is total. We provide a secure, confidential environment for our UHNW clients and family offices."
}, {
  title: "Seamless Execution",
  icon: Clock,
  description: "Precision-timed logistics and white-glove service. We manage every detail of your flight profile with meticulous care."
}];
export default function WhyChooseOurService() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="125014" id="the-starboard-advantage" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: `
            linear-gradient(var(--color-primary) 1px, transparent 1px),
            linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)
          `,
      backgroundSize: "60px 60px"
    }} aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={sectionRef} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className={`relative h-[500px] lg:h-[700px] rounded-sm overflow-hidden border border-white/10 shadow-2xl transition-all duration-1000 ease-out ${sectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="absolute inset-0 bg-primary/5 mix-blend-overlay z-10" />
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618318/img/delivery_driver_smiling.png" alt="Starboard Aviation Group elite flight crew" className="w-full h-full object-cover grayscale brightness-75" />
            
            {}
            
          </div>

          {}
          <div className={`transition-all duration-1000 delay-300 ease-out ${sectionVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="mb-16">
              <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tighter text-foreground uppercase">
                The <span className="text-primary">Starboard</span> Advantage
              </h2>
              <p className="text-xl text-muted-foreground max-w-xl leading-relaxed font-light">
                We are redefining private aviation with a focus on technical mastery, absolute transparency, and a white-glove approach to aircraft management. Experience the elite standard of flight.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {benefits.map((benefit, i) => <Card key={i} data-index={i} className="bg-transparent border border-white/5 hover:border-primary/40 transition-all duration-500 shadow-none group rounded-sm">
                  <CardHeader className="pb-4">
                    <div className="size-14 rounded-sm bg-white/5 flex items-center justify-center mb-6 group-hover:bg-primary/10 transition-colors duration-500">
                      <benefit.icon className="size-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground uppercase tracking-tight">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed text-sm font-light">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}